// client/src/pages/BulkQRCreator.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import qrcode from 'qrcode';
import QRCode from 'qrcode.react';
import { Link } from 'react-router-dom';

function BulkQRCreator() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logo, setLogo] = useState(null);
  const [qrCodeLimit, setQrCodeLimit] = useState(3); // Default free plan limit
  const [daysRemaining, setDaysRemaining] = useState(14);
  const [qrCodesCreated, setQrCodesCreated] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  
  const [qrStyle, setQrStyle] = useState({
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF',
    shape: 'square',
    dotStyle: 'square',
    showName: true,
    showURL: false,
    textColor: '#000000'
  });

  // Add URL+Slug combination helper function
  const combineUrlAndSlug = (url, slug) => {
    if (!slug) return url;
    
    let baseUrl = url;
    
    // Add protocol if missing
    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://') && 
        !baseUrl.startsWith('tel:') && !baseUrl.startsWith('mailto:') && 
        !baseUrl.startsWith('sms:')) {
      baseUrl = 'https://' + baseUrl;
    }
    
    // Normalize URL - ensure it doesn't end with a slash if we're adding a slug
    if (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    }
    
    // Normalize slug - ensure it starts with a slash
    let normalizedSlug = slug;
    if (!normalizedSlug.startsWith('/')) {
      normalizedSlug = '/' + normalizedSlug;
    }
    
    // Combine them
    const combinedUrl = baseUrl + normalizedSlug;
    
    console.log(`Combined URL: ${baseUrl} + ${normalizedSlug} = ${combinedUrl}`);
    
    return combinedUrl;
  };



  // Fetch account info on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!user) return;
        
        console.log("Fetching data for user:", user.id);
        
        // First, get the user document to find the account_id
        const userResponse = await fetch(`/api/users/by-clerk/${user.id}`);
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          console.log('User data:', userData);
          
          if (userData && userData.account_id) {
            // Now fetch the account using the account_id
            const accountResponse = await fetch(`/api/account/${userData.account_id}`);
            
            if (accountResponse.ok) {
              const accountData = await accountResponse.json();
              console.log('Account data:', accountData);
              setAccount(accountData);
              
              // Set QR code limit based on plan
              if (accountData.plan === 'basic_monthly') {
                setQrCodeLimit(3);
              } else if (accountData.plan && accountData.plan.startsWith('free_')) {
                setQrCodeLimit(3);
                
                // Calculate days remaining if on free plan
                // const creationDate = new Date(accountData.createdAt);
                // const currentDate = new Date();
                // const timeDiff = (14 * 24 * 60 * 60 * 1000) - (currentDate - creationDate);
                // const daysRemaining = Math.max(0, Math.ceil(timeDiff / (24 * 60 * 60 * 1000)));
                // setDaysRemaining(daysRemaining);
              } else if (accountData.plan && accountData.plan.startsWith('basic_')) {
                setQrCodeLimit(3);
              } else if (accountData.plan && accountData.plan.startsWith('plus_')) {
                setQrCodeLimit(100);
              } else {
                setQrCodeLimit(500); // Unlimited for business plans
              }
            }
          }
        }
        
        // Fetch QR codes to count them
        const qrResponse = await fetch(`/api/qrcodes?user_id=${user.id}`);
        if (qrResponse.ok) {
          const qrData = await qrResponse.json();
          console.log(`Found ${qrData.length} QR codes for user ${user.id}`);
          setQrCodesCreated(qrData.length);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    if (user) {
      const fetchAccount = async () => {
        try {
          const userResponse = await fetch(`/api/users/by-clerk/${user.id}`);
          if (userResponse.ok) {
            const userData = await userResponse.json();
            if (userData && userData.account_id) {
              const accountResponse = await fetch(`/api/account/${userData.account_id}`);
              if (accountResponse.ok) {
                const accountData = await accountResponse.json();
                setAccount(accountData);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching account:', error);
        }
      };
      fetchAccount();
    }
    fetchUserData();
  }, [user]);

  // Handle CSV export template - updated with better slug examples
  const handleExportTemplate = () => {
    // Create a sample CSV template with better explanations
    const csvContent = [
      ['Name (optional)', 'URL', 'Slug (optional)'],
      ['Product One', 'example.com', '/product-1'],
      ['Product Two', 'example.com', '/deviceID'],
      ['Product Three', 'mywebsite.com', '/special-offer'],
      ['', 'Note: Slugs will be appended to URLs', ''],
      ['', 'URLs can be bulk updated later but slugs can not', '']
    ];
    
    // Convert to CSV string
    const csv = Papa.unparse(csvContent);
    
    // Create a Blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'qr_code_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogo(URL.createObjectURL(e.target.files[0]));
    }
  };
  // Handle CSV file upload and parsing - improved to properly handle slugs
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          // Validate CSV structure
          const hasRequiredColumns = results.meta.fields.includes('URL');
          
          if (!hasRequiredColumns) {
            setError('CSV file must contain a URL column. Please use the template.');
            setParsedData([]);
            return;
          }
          
          // Process and validate data
          const processedData = results.data.map(row => {
            const processedRow = {...row};
            
            // Handle Slug or Slug (optional)
            if (row['Slug (optional)'] !== undefined) {
              processedRow.Slug = row['Slug (optional)'];
            } else if (row.Slug !== undefined) {
              // Already has the right field name
            } else {
              processedRow.Slug = '';
            }
            
            return processedRow;
          });
          
          // Log available columns and processed data
          console.log('CSV columns found:', results.meta.fields);
          console.log('Processed data:', processedData);
          
          setParsedData(processedData);
          setError('');
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          setError('Error parsing CSV file. Please check the format and try again.');
          setParsedData([]);
        }
      });
    }
  };

  const uploadLogoToServer = async (file) => {
    if (!file) {
      console.error('No file provided to uploadLogoToServer');
      return null;
    }
    
    console.log('Preparing to upload logo file:', file.name, file.type, file.size);
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      console.log('Sending logo to server...');
      
      const response = await fetch('/api/qrcodes/upload-logo', {
        method: 'POST',
        body: formData
      });
      
      console.log('Upload response status:', response.status);
      
      // Handle non-OK responses
      if (!response.ok) {
        let errorMessage = `Upload failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error('Server error:', errorData);
        } catch (e) {
          const text = await response.text();
          console.error('Could not parse error response:', text);
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('Upload result:', result);
      
      if (!result.success || !result.url) {
        console.error('Invalid response format:', result);
        throw new Error('Invalid response from server');
      }
      
      return result.url;
    } catch (error) {
      console.error('Error uploading logo:', error);
      // Don't throw, just return null to allow the process to continue without a logo
      return null;
    }
  };

  const handleCreateQRCodes = async () => {
    if (!parsedData || parsedData.length === 0) {
      setError('Please upload a valid CSV file first.');
      return;
    }
    
    if (!groupName.trim()) {
      setError('Please provide a name for this QR code group.');
      return;
    }
    
    
    if (parsedData.length > 200) {
      setError('Maximum of 200 QR codes allowed per batch.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // New step: Upload logo to Imgur first if it exists
      let imgurLogoUrl = null;
      if (logoFile) {
        console.log('Uploading logo file to server...');
        imgurLogoUrl = await uploadLogoToServer(logoFile);
        
        if (imgurLogoUrl) {
          console.log('Logo uploaded successfully:', imgurLogoUrl);
        } else {
          console.warn('Logo upload failed, continuing without logo');
        }
      }
      
      // Get account ID
      const accountId = account?._id || (await getAccountId());
      
      // Create a zip file for download
      const zip = new JSZip();
      const folder = zip.folder(groupName);
      
      // Step 1: Create the bulk group first
      const bulkGroupData = {
        name: groupName,
        account_id: accountId,
        user_id: user.id,
        qr_count: parsedData.length,
        shape: qrStyle.shape,
        foregroundColor: qrStyle.foregroundColor,
        backgroundColor: qrStyle.backgroundColor,
        textColor: qrStyle.textColor,
        showName: qrStyle.showName,
        showURL: qrStyle.showURL
      };
      
      console.log('Creating bulk group with data:', bulkGroupData);
      
      const bulkGroupResponse = await fetch('/api/bulk-qrcodes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bulkGroupData),
      });
      
      if (!bulkGroupResponse.ok) {
        const errorText = await bulkGroupResponse.text();
        throw new Error('Failed to create bulk group: ' + errorText);
      }
      
      // Use the MongoDB _id as the bulk_id
      const bulkGroup = await bulkGroupResponse.json();
      const bulkId = bulkGroup._id;
      
      console.log('Created bulk group with MongoDB ID:', bulkId);
      
      // Step 2: Process QR codes
      const qrCodeBatch = [];
      // Keep track of used filenames to avoid duplicates
      const usedFilenames = new Set();
      
      // Process each row in CSV and generate QR codes for the ZIP file
      for (const row of parsedData) {
        if (!row.URL) continue;
        
        // Format URL properly with validation
        let baseUrl = row.URL.trim();
        
        // Process the slug if it exists
        const slug = row.Slug ? row.Slug.trim() : '';
        
        // Create a normalized version of the slug for storage
        let normalizedSlug = '';
        if (slug) {
          normalizedSlug = slug.startsWith('/') ? slug : '/' + slug;
        }
        
        // Add protocol if missing
        if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://') && 
            !baseUrl.startsWith('tel:') && !baseUrl.startsWith('mailto:') && 
            !baseUrl.startsWith('sms:')) {
          baseUrl = 'https://' + baseUrl;
        }
        
        // Combine URL and slug for the final targetUrl
        let targetUrl = baseUrl;
        if (normalizedSlug) {
          targetUrl = combineUrlAndSlug(baseUrl, normalizedSlug);
          console.log(`Created combined URL: ${targetUrl}`);
          console.log(`Original URL: ${baseUrl}, Slug: ${normalizedSlug}`);
        }
        
        // Handle different possible column names for Name
        let name = '';
        if (row["Name (optional)"]) {
          name = row["Name (optional)"].trim();
        } else if (row["Name"]) {
          name = row["Name"].trim();
        }
        
        // Format URL for display and filename generation
        let urlForName = targetUrl.replace(/^https?:\/\//, '').split('/')[0];
        
        // Generate fallback QR name - NEVER use an empty string
        const qrName = name || normalizedSlug || urlForName || 'QR Code';
        
        // Generate a base filename
        let baseFileName = '';
        if (name) {
          baseFileName = name.replace(/[^a-z0-9]/gi, '_');
        } else if (normalizedSlug) {
          baseFileName = normalizedSlug.replace(/[^a-z0-9]/gi, '_');
        } else {
          baseFileName = urlForName.replace(/[^a-z0-9]/gi, '_');
        }
        
        // Create a unique filename
        let fileName = baseFileName;
        let counter = 1;
        
        // If filename already exists, add numbering (1), (2), etc.
        while (usedFilenames.has(fileName)) {
          fileName = `${baseFileName}_(${counter})`;
          counter++;
        }
        
        // Add to used filenames set
        usedFilenames.add(fileName);
        
        // Determine if we should show the name based on whether a name exists
        const shouldShowName = name.length > 0 && qrStyle.showName;
        
        // Add to batch array for database
        const qrCodeData = {
          user_id: user.id,
          email_address: user.primaryEmailAddress.emailAddress,
          qr_name: qrName, 
          targetUrl: targetUrl,
          bulk_id: bulkId,
          bulk_name: groupName,
          shape: qrStyle.shape,
          dotStyle: qrStyle.dotStyle || qrStyle.shape,
          foregroundColor: qrStyle.foregroundColor,
          backgroundColor: qrStyle.backgroundColor,
          textColor: qrStyle.textColor,
          showName: shouldShowName,
          showURL: qrStyle.showURL,
          slug: normalizedSlug,
          account_id: accountId
        };
        
        // Use the Imgur URL for the logo instead of the blob URL
        if (imgurLogoUrl) {
          qrCodeData.logo = imgurLogoUrl;
        }
        
        qrCodeBatch.push(qrCodeData);
        
        // Log each QR code for debugging
        console.log(`Added QR code to batch: ${qrName}`);
        console.log(`  Target URL: ${targetUrl}`);
        console.log(`  Slug: ${normalizedSlug}`);
        console.log(`  Logo: ${qrCodeData.logo}`);
        
        // Create a high-resolution canvas
        const scaleFactor = 2;
        const baseSize = 800;
        const size = baseSize * scaleFactor;
        
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Scale all drawing operations
        ctx.scale(scaleFactor, scaleFactor);
        
        // Get styling info
        const shape = qrStyle.shape || 'square';
        const dotStyle = qrStyle.dotStyle || shape;
        const bgColor = qrStyle.backgroundColor || '#f1f1f4';
        const fgColor = qrStyle.foregroundColor || '#000000';
        const textColor = qrStyle.textColor || '#000000';
        
        // Draw background with shape
        ctx.fillStyle = bgColor;
        if (shape === 'circle') {
          ctx.beginPath();
          ctx.arc(baseSize/2, baseSize/2, baseSize/2, 0, Math.PI * 2);
          ctx.fill();
        } else if (shape === 'rounded') {
          const radius = baseSize / 10;
          ctx.beginPath();
          ctx.moveTo(radius, 0);
          ctx.lineTo(baseSize - radius, 0);
          ctx.quadraticCurveTo(baseSize, 0, baseSize, radius);
          ctx.lineTo(baseSize, baseSize - radius);
          ctx.quadraticCurveTo(baseSize, baseSize, baseSize - radius, baseSize);
          ctx.lineTo(radius, baseSize);
          ctx.quadraticCurveTo(0, baseSize, 0, baseSize - radius);
          ctx.lineTo(0, radius);
          ctx.quadraticCurveTo(0, 0, radius, 0);
          ctx.fill();
        } else {
          ctx.fillRect(0, 0, baseSize, baseSize);
        }
        
        // Generate QR code on a temporary canvas
        await new Promise((resolve) => {
          const tempCanvas = document.createElement('canvas');
          qrcode.toCanvas(tempCanvas, targetUrl, { // Use the combined URL+slug
            errorCorrectionLevel: 'H',
            margin: 0,
            scale: 1,
            width: 100  // Small size to get data
          }, function(error) {
            if (error) {
              console.error("QR generation error:", error);
              resolve();
              return;
            }
            
            // Get QR code data
            const tempCtx = tempCanvas.getContext('2d');
            const qrData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            const data = qrData.data;
            const moduleCount = tempCanvas.width;
            
            // Calculate dot size based on canvas size
            const moduleSize = Math.floor(baseSize * 0.7 / moduleCount);
            const startPoint = Math.floor((baseSize - (moduleCount * moduleSize)) / 2);
            
            // Draw custom dots
            ctx.fillStyle = fgColor;
            
            for (let y = 0; y < moduleCount; y++) {
              for (let x = 0; x < moduleCount; x++) {
                // Get pixel color (only check red channel since QR is black/white)
                const pixelIndex = (y * moduleCount + x) * 4;
                const isDark = data[pixelIndex] < 128;
                
                if (isDark) {
                  const xPos = startPoint + x * moduleSize;
                  const yPos = startPoint + y * moduleSize;
                  
                  // Draw based on selected dot style
                  if (dotStyle === 'circle') {
                    // Draw circular dots
                    ctx.beginPath();
                    ctx.arc(
                      xPos + moduleSize / 2, 
                      yPos + moduleSize / 2, 
                      moduleSize / 2 * 0.9, // Slightly smaller for spacing
                      0, 
                      Math.PI * 2
                    );
                    ctx.fill();
                  } else if (dotStyle === 'rounded') {
                    // Draw rounded square dots
                    const radius = moduleSize / 4;
                    ctx.beginPath();
                    ctx.moveTo(xPos + radius, yPos);
                    ctx.lineTo(xPos + moduleSize - radius, yPos);
                    ctx.quadraticCurveTo(xPos + moduleSize, yPos, xPos + moduleSize, yPos + radius);
                    ctx.lineTo(xPos + moduleSize, yPos + moduleSize - radius);
                    ctx.quadraticCurveTo(xPos + moduleSize, yPos + moduleSize, xPos + moduleSize - radius, yPos + moduleSize);
                    ctx.lineTo(xPos + radius, yPos + moduleSize);
                    ctx.quadraticCurveTo(xPos, yPos + moduleSize, xPos, yPos + moduleSize - radius);
                    ctx.lineTo(xPos, yPos + radius);
                    ctx.quadraticCurveTo(xPos, yPos, xPos + radius, yPos);
                    ctx.fill();
                  } else {
                    // Default square dots
                    ctx.fillRect(xPos, yPos, moduleSize, moduleSize);
                  }
                }
              }
            }
            
            // Add logo if available - use imgurLogoUrl instead of blob URL
            if (imgurLogoUrl) {
              const logoImg = new Image();
              logoImg.crossOrigin = "Anonymous";
              logoImg.src = imgurLogoUrl;
              
              logoImg.onload = function() {
                // Calculate logo size and position
                const logoSize = baseSize * 0.15; // 15% of QR code size
                const logoX = (baseSize - logoSize) / 2;
                const logoY = (baseSize - logoSize) / 2;
                
                // Create a square background with padding for the logo
                const padding = baseSize * 0.025; // Padding around the logo
                ctx.fillStyle = bgColor;
                ctx.fillRect(
                  logoX - padding, 
                  logoY - padding, 
                  logoSize + (padding * 2), 
                  logoSize + (padding * 2)
                );
                
                // Draw logo
                ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
                
                finishQR();
              };
              
              logoImg.onerror = function() {
                console.error("Error loading logo from Imgur:", imgurLogoUrl);
                finishQR();
              };
            } else {
              finishQR();
            }
            
            function finishQR() {
              // Add text elements to QR code
              const fontFamily = "'Arial', sans-serif";
              
              // Calculate positions
              let namePosition = baseSize - 100;
              let urlPosition = baseSize - 50;
              if (shouldShowName) {
                // Limit the name to 25 characters with ellipsis if longer
                const maxNameLength = 25;
                const displayName = qrName.length > maxNameLength 
                  ? qrName.substring(0, maxNameLength) + '...' 
                  : qrName;
                  
                ctx.fillStyle = textColor;
                ctx.font = `bold 48px ${fontFamily}`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(displayName, baseSize/2, namePosition);
              }
            
              // Add URL if enabled
              if (qrStyle.showURL) {
                // Determine what to display as the URL text
                let displayText = targetUrl;
                
                // Format display text based on URL type
                if (targetUrl.startsWith('tel:')) {
                  displayText = targetUrl.substring(4); // Remove 'tel:' prefix
                } else if (targetUrl.startsWith('sms:')) {
                  displayText = targetUrl.substring(4); // Remove 'sms:' prefix
                } else if (targetUrl.startsWith('mailto:')) {
                  displayText = targetUrl.substring(7); // Remove 'mailto:' prefix
                }
                
                // Limit URL length
                const maxUrlLength = 40;
                if (displayText.length > maxUrlLength) {
                  displayText = displayText.substring(0, maxUrlLength) + '...';
                }
                
                ctx.fillStyle = textColor;
                const urlFontSize = Math.floor(baseSize * 0.025);
                ctx.font = `${urlFontSize}px ${fontFamily}`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(displayText, baseSize/2, urlPosition);
              }
              
              // Convert canvas to image and add to zip
              const dataUrl = canvas.toDataURL('image/png');
              const base64Data = dataUrl.split(',')[1];
              folder.file(`${fileName}.png`, base64Data, {base64: true});
              
              resolve();
            }
          });
        });
      }
      
      // Step 3: Save QR codes to database
      console.log('Sending QR code batch to API:', qrCodeBatch.length, 'items');
      
      const response = await fetch('/api/qrcodes/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qrCodes: qrCodeBatch,
          account_id: accountId
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(` ${errorData.error || response.statusText}`);
      }
      
      const result = await response.json();
      
      // Generate and download zip file
      // const zipContent = await zip.generateAsync({type: 'blob'});
      // saveAs(zipContent, `${groupName}_QR_Codes.zip`);
      
      setSuccess(`Successfully created ${result.count} QR codes! Download them on the dashboard.`);
      
      // Clear form
      setCsvFile(null);
      setParsedData([]);
      setGroupName('');
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      
    } catch (error) {
      setError(`${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  

  
  // Get account ID if not available
  const getAccountId = async () => {
    try {
      const userResponse = await fetch(`/api/users/by-clerk/${user.id}`);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        return userData.account_id;
      }
      throw new Error('Could not find account');
    } catch (error) {
      console.error('Error getting account ID:', error);
      throw error;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="mt-6 text-3xl font-bold mb-6">Bulk QR Creator</h1>
      
<div className="flex justify-between items-center mb-6">
  <div className="mr-4">
    <p className="text-sm font-medium text-gray-700">
      QR Codes Created: <span className="font-bold">{qrCodesCreated} of {qrCodeLimit}</span>
    </p>
  </div>
  
  <div className="flex-grow">
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div 
        className="bg-blue-600 h-2.5 rounded-full" 
        style={{ width: `${Math.min(qrCodesCreated / qrCodeLimit * 100, 100)}%` }}
      ></div>
    </div>
  </div>
  
  {account && qrCodesCreated >= qrCodeLimit && (
    <div className="ml-4">
      <Link 
        to="/upgrade" 
        className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
      >
        Max Reached. Upgrade
      </Link>
    </div>
  )}
</div>

      <div className="bg-blue-50 rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-xl text-blue-600 font-semibold mb-4">Create Hundreds of QR Codes in Minutes</h2>
        
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="mb-2 md:mb-0 md:mr-4">
              <h3 className="text-sm font-semibold text-blue-600">1. Download Template</h3>
              <div className="mt-2 border-l-4 border-blue-200 pl-4 py-1">
                <p className="text-sm text-blue-600 italic">
                  <span className="font-semibold">Tip:</span> Have a complex list of IDs or links? 
                  Our AI scanner can automatically generate an export file for you.
                </p>
              </div>
              <button
                onClick={handleExportTemplate}
                className="mt-5 bg-blue-700 hover:bg-blue-800 text-white py-2 px-4 rounded transition duration-200 text-sm font-semibold shrink-0"
              >
                Download Template
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <div>
              <h3 className="text-sm font-semibold text-blue-600">2. Fill & Upload Your CSV</h3>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-blue-600">3. Name & Customize Your QR Group</h3>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-blue-600">4. Create & Manage</h3>
              <div className="mt-2 border-l-4 border-blue-200 pl-4 py-1">
                <p className="text-sm text-blue-600 italic">
                  <span className="font-semibold">Tip:</span> You can update the target URLs 
                  for the entire group at any time without needing to change your QR codes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="block mt-2 text-m text-gray-700 font-semibold mb-1">Upload CSV</h2>
        <p className="text-sm text-gray-400 mb-4">
          Upload your completed CSV file with URLs and slugs. Larger CSVs will take more time to process.
        </p>
        <div className="border-2 border-dashed bg-blue-50 border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
            id="csv-upload"
          />
          <label
            htmlFor="csv-upload"
            className="cursor-pointer text-white font-semibold bg-blue-700 hover:bg-blue-800 py-3 px-5 rounded inline-block"
          >
            Select CSV File
          </label>
          {csvFile && (
            <p className="mt-3 text-green-600">
              File selected: {csvFile.name} ({parsedData.length} records)
            </p>
          )}
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
          {/* Form section */}
          <div className="md:col-span-5 bg-white rounded-lg">
            <label className="block mt-1 text-m text-gray-700 font-semibold mb-1">Group Name (required):</label>
            <p className="text-sm text-gray-400 mt-1">Create a name for your group, all qr codes will be added to this group. </p>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="w-full px-4 py-2 mt-1 mb-5 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="My QR code group"
            />
            <div className="flex space-x-4 mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showName"
                  checked={qrStyle.showName}
                  onChange={(e) => setQrStyle({...qrStyle, showName: e.target.checked})}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="showName" className="text-gray-700">Show Name(s) in CSV</label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showURL"
                  checked={qrStyle.showURL}
                  onChange={(e) => setQrStyle({...qrStyle, showURL: e.target.checked})}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="showURL" className="text-gray-700">Show URL(s) in CSV</label>
              </div>
           
            </div>
            {parsedData.length > 0 && parsedData[0].Slug && (
    <div className="mt-3 p-3 bg-blue-50 rounded-lg w-full">
      <div className="text-sm text-gray-700 font-medium">URL Preview:</div>
      <div className="mt-1 font-mono text-xs break-all">
        {(() => {
          let baseUrl = parsedData[0].URL.trim();
          if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
            baseUrl = 'https://' + baseUrl;
          }
          const slug = parsedData[0].Slug.trim();
          return combineUrlAndSlug(baseUrl, slug);
        })()}
      </div>
      <div className="mt-2 text-xs text-gray-600">
        This is how your QR code URL will be constructed from URL + Slug
      </div>
    </div>
  )}

            <label className="pt-3 block mt-5 text-m text-gray-700 font-semibold mb-1">Customize colors:</label>

            {/* Color selectors */}
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-gray-700 font-medium mb-2">QR Code:</label>
                <div className="flex items-center">
                  <input
                    type="color"
                    className="w-10 h-10 font-medium border rounded mr-2"
                    value={qrStyle.foregroundColor}
                    onChange={(e) => setQrStyle({...qrStyle, foregroundColor: e.target.value})}
                  />
                  <input
                    type="text"
                    className="flex-grow w-full h-10 text-sm font-medium px-3 py-2 border rounded"
                    value={qrStyle.foregroundColor}
                    onChange={(e) => setQrStyle({...qrStyle, foregroundColor: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 font-medium mb-2">Background:</label>
                <div className="flex items-center">
                  <input
                    type="color"
                    className="w-10 h-10 font-medium border rounded mr-2"
                    value={qrStyle.backgroundColor}
                    onChange={(e) => setQrStyle({...qrStyle, backgroundColor: e.target.value})}
                  />
                  <input
                    type="text"
                    className="w-full h-10 text-sm flex-grow font-medium px-3 py-2 border rounded"
                    value={qrStyle.backgroundColor}
                    onChange={(e) => setQrStyle({...qrStyle, backgroundColor: e.target.value})}
                  />
                </div>
              </div>
                
              <div>
                <label className="block text-sm text-gray-700 font-medium mb-2">Text:</label>
                <div className="flex items-center">
                  <input
                    type="color"
                    className="w-10 h-10 font-medium border rounded mr-1"
                    value={qrStyle.textColor}
                    onChange={(e) => setQrStyle({...qrStyle, textColor: e.target.value})}
                  />
                  <input
                    type="text"
                    className="w-full h-10 flex-grow text-sm font-medium px-3 py-2 border rounded"
                    value={qrStyle.textColor}
                    onChange={(e) => setQrStyle({...qrStyle, textColor: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="pt-3 block mt-5 text-m text-gray-700 font-semibold mb-2">Add Logo or Image:</label>
              <input
                type="file"
                accept="image/*"
                className="w-full"
                onChange={handleLogoChange}
                name="logo"
              />
            </div>
          </div>

          {/* Preview section */}
<div className="md:col-span-3 flex flex-col items-center justify-center bg-white rounded-lg p-5 mb-6">
  {/* QR Code Preview with consistent styling */}
  <div 
  className={`qr-container p-10 ${qrStyle.shape === 'circle' ? 'rounded-full' : qrStyle.shape === 'rounded' ? 'rounded-2xl' : ''}`}
  style={{ 
    backgroundColor: qrStyle.backgroundColor || '#f1f1f4',
    ...(qrStyle.shape === 'circle' ? {
      width: '310px',  
      height: '310px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    } : {})
  }}
>
  <div className="relative">
    <QRCode 
      value={(() => {
        // Default URL if no data
        if (!parsedData.length || !parsedData[0].URL) {
          return "https://example.com";
        }
        
        // Start with the URL from first row
        let previewUrl = parsedData[0].URL.trim();
        
        // Add protocol if missing
        if (!previewUrl.startsWith('http://') && !previewUrl.startsWith('https://') && 
            !previewUrl.startsWith('tel:') && !previewUrl.startsWith('mailto:') && 
            !previewUrl.startsWith('sms:')) {
          previewUrl = 'https://' + previewUrl;
        }
        
        // Add slug if present using the same helper function
        const previewSlug = parsedData[0].Slug ? parsedData[0].Slug.trim() : '';
        if (previewSlug) {
          previewUrl = combineUrlAndSlug(previewUrl, previewSlug);
        }
        
        return previewUrl;
      })()}
      size={150}
      fgColor={qrStyle.foregroundColor}
      bgColor="transparent"
      level="H"
      renderAs="svg"
    />
    
    {/* Logo overlay */}
    {logo && (
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Logo background */}
        <div 
          className={`${
            qrStyle.shape === 'circle' ? 'rounded-full' : 
            qrStyle.shape === 'rounded' ? 'rounded-lg' : 
            'rounded-none'
          }`}
          style={{
            width: '60px', 
            height: '60px', 
            backgroundColor: qrStyle.backgroundColor,
            position: 'absolute',
            zIndex: 5
          }}
        />
        
        {/* Logo image */}
        <img 
          src={logo} 
          alt="Logo" 
          style={{
            width: '40px',
            height: '40px',
            zIndex: 10,
            position: 'absolute'
          }}
        />
      </div>
    )}
  </div>

  {/* Show name if selected */}
  {qrStyle.showName && (
    <div 
      className="text-center text-lg font-bold mt-3" 
      style={{ color: qrStyle.textColor || "#000000" }}
    >
      {parsedData.length > 0 && parsedData[0].Name ? parsedData[0].Name : "Names in CSV"}
    </div>
  )}
  
  {/* Show URL if selected */}
  {qrStyle.showURL && (
    <div 
      className="text-center text-m font-semibold mt-1"
      style={{ color: qrStyle.textColor || "#000000" }}
    >
      {(() => {
        if (!parsedData.length || !parsedData[0].URL) {
          return "URLs in CSV";
        }
        
        let displayUrl = parsedData[0].URL.trim();
        if (displayUrl.length > 30) {
          displayUrl = 'URLs in CSV';
        }
        
        return displayUrl;
      })()}
    </div>
  )}
</div>
  {/* <h2 className="mt-1 text-sm text-gray-500 font-medium mb-2">Preview</h2> */}

  {/* Add this new section to show combined URL preview */}
  <div>

    </div>


            {/* Add this new section to show combined URL preview
            {parsedData.length > 0 && parsedData[0].Slug && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg w-full">
                <div className="text-sm text-gray-700 font-medium">URL Preview:</div>
                <div className="mt-1 font-mono text-xs break-all">
                  {(() => {
                    let baseUrl = parsedData[0].URL.trim();
                    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
                      baseUrl = 'https://' + baseUrl;
                    }
                    const slug = parsedData[0].Slug.trim();
                    return combineUrlAndSlug(baseUrl, slug);
                  })()}
                </div>
                <div className="mt-2 text-xs text-gray-600">
                  This is how your QR code URL will be constructed from URL + Slug
                </div>
              </div>
            )} */}

            <h2 className="mt-3 text-sm text-gray-400 font-medium mb-2">Preview only</h2>
            <h2 className="text-sm text-gray-400 font-medium mb-2">QR codes will be generated with CSV content</h2>
            <div className="mt-6 w-full">
              <h3 className="block mt-5 text-m text-gray-700 font-semibold mb-2">Select one:</h3>

              <div className="grid grid-cols-3 gap-2 md:gap-4">
                <div 
                  className={`flex flex-col items-center cursor-pointer p-2 md:p-3 border rounded-lg ${qrStyle.shape === 'square' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                  onClick={() => setQrStyle({...qrStyle, shape: 'square'})}
                >
                  <div className="flex items-center justify-center mb-2 w-12 h-12 md:w-12 md:h-12" 
                       style={{backgroundColor: qrStyle.backgroundColor}}>
                    <div className="w-5 h-5 md:w-8 md:h-8" style={{backgroundColor: qrStyle.foregroundColor}}></div>
                  </div>
                  <span className="block text-xs md:text-sm text-gray-700 font-medium text-center mb-1">Square</span>
                  <span className="text-xs text-gray-500 hidden md:inline">(Preview)</span>
                </div>
                          
                <div 
                  className={`flex flex-col items-center cursor-pointer p-2 md:p-3 border rounded-lg ${qrStyle.shape === 'rounded' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                  onClick={() => setQrStyle({...qrStyle, shape: 'rounded'})}
                >
                  <div className="flex items-center justify-center mb-2 w-16 h-16 md:w-12 md:h-12 rounded-lg" 
                       style={{backgroundColor: qrStyle.backgroundColor}}>
                    <div className="w-10 h-10 md:w-8 md:h-8 rounded-lg" style={{backgroundColor: qrStyle.foregroundColor}}></div>
                  </div>
                  <span className="block text-xs md:text-sm text-gray-700 font-medium text-center mb-1">Rounded</span>
                  <span className="text-xs text-gray-500 hidden md:inline">(Preview)</span>
                </div>
                          
                <div 
                  className={`flex flex-col items-center cursor-pointer p-2 md:p-3 border rounded-lg ${qrStyle.shape === 'circle' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                  onClick={() => setQrStyle({...qrStyle, shape: 'circle'})}
                >
                  <div className="flex items-center justify-center mb-2 w-16 h-16 md:w-12 md:h-12 rounded-full" 
                       style={{backgroundColor: qrStyle.backgroundColor}}>
                    <div className="w-10 h-10 md:w-8 md:h-8 rounded-full" style={{backgroundColor: qrStyle.foregroundColor}}></div>
                  </div>
                  <span className="block text-xs md:text-sm text-gray-700 font-medium text-center mb-1">Circle</span>
                  <span className="text-xs text-gray-500 hidden md:inline">(Preview)</span>
                </div>
              </div>
            </div>
          </div>
      
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6">
              {success}
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-md">
        <div className="container mx-auto max-w-screen-lg">
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6">
              {success}
            </div>
          )}

          <div className="flex justify-end max-w-5xl mx-auto"> 
          <div className="pt-3 text-gray-900 mr-3 "> ({parsedData.length || 0}) QR Codes</div> 
            {qrCodesCreated >= qrCodeLimit ? (
              <Link 
                to="/upgrade" 
                className="px-6 py-3 rounded font-semibold transition duration-200 bg-blue-700 hover:bg-blue-800 text-white"
              >
                Upgrade to Create
              </Link>
            ) : (
              /* Normal create button if user is under their limit */
              <button
                onClick={handleCreateQRCodes}
                disabled={isLoading}
                className={`bg-blue-700 hover:bg-gray-800 text-white py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                  (isLoading ) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⟳</span>
                    Processing...
                  </>
                ) : (
                  `Create QR Group`
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkQRCreator;